import { EventEmitter } from '@timing71/common';
import { Decoder, Encoder } from 'socket.io-parser';

const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_'.split('');
const length = 64;
let prev, seed = 0;

const V4_RECORD_SEPARATOR = '\x1e';

function encode(num) {
  var encoded = '';

  do {
    encoded = alphabet[num % length] + encoded;
    num = Math.floor(num / length);
  } while (num > 0);

  return encoded;
}

function yeast() {
  var now = encode(+new Date());
  if (now !== prev) {
    seed = 0;
    prev = now;
    return prev;
  }
  return now + '.' + encode(seed++);
}

const splitV3Payload = (data) => {
  // Based on https://github.com/socketio/engine.io-parser/blob/2.2.x/lib/browser.js#L371
  const messages = [];
  let length = '', n, msg;

  for (var i = 0, l = data.length; i < l; i++) {
    var chr = data.charAt(i);

    if (chr !== ':') {
      length += chr;
      continue;
    }

    if (length === '' || (length != (n = Number(length)))) { // eslint-disable-line eqeqeq
      // parser error - ignoring payload
      return messages;
    }

    msg = data.substr(i + 1, n);

    if (length != msg.length) { // eslint-disable-line eqeqeq
      // parser error - ignoring payload
      return messages;
    }

    if (msg.length) {
      messages.push(msg);
    }

    // advance cursor
    i += n;
    length = '';
  }
  return messages;
};

export class SocketIOClient extends EventEmitter {

  constructor(host, connectionService, options={}) {
    super();
    this.protocolVersion = options.protocolVersion || 4;
    const socketURL = `${host}/socket.io/?EIO=${this.protocolVersion}`;
    this.pollingUrl = `http${options.forceInsecure ? '' : 's'}://${socketURL}&transport=polling`;
    this.wsUrl = `ws${options.forceInsecure ? '' : 's'}://${socketURL}&transport=websocket`;
    this.options = options;

    this.connectionService = connectionService;

    this.doPoll = this.doPoll.bind(this);
    this.doWebsocket = this.doWebsocket.bind(this);
    this.handlePacket = this.handlePacket.bind(this);
    this.splitPayload = this.splitPayload.bind(this);

    this.sid = null;

    this.ws = null;
    this._pollTimeout = null;
    this.polling = true;
    this.usingWebsocket = !!options.forceWebsocket;
    this.forcedPoll = false;

    this._pollDecoder = new Decoder();
    this._pollDecoder.on('decoded', (p) => this.handlePacket(false, p));

    this._wsDecoder = new Decoder();
    this._wsDecoder.on('decoded', (p) => this.handlePacket(true, p));

    this._encoder = new Encoder();

    this._rooms = {};
    this._roomsIndex = 0;

    if (this.usingWebsocket) {
      this.doWebsocket();
    }
    else {
      this.doPoll();
    }
  }

  splitPayload(rawData) {
    if (this.protocolVersion === 3) {
      return splitV3Payload(rawData);
    }
    else if (this.protocolVersion === 4) {
      return rawData.split(V4_RECORD_SEPARATOR);
    }
    console.warn(`Unable to handle protocol version ${this.protocolVersion}`);
  }

  doPoll() {
    if (this.polling) {
      let myUrl = `${this.pollingUrl}&t=${yeast()}`;
      if (this.sid) {
        myUrl += `&sid=${this.sid}`;
      }
      this.connectionService.fetch(myUrl).then(
        pollData => {
          this.splitPayload(pollData).forEach(
            msg => this._pollDecoder.add(msg)
          );
        }
      ).catch(
        () => {
          this.sid = null;
          this.forcedPoll = false;
        }
      ).finally(
        () => {
          if (this._pollTimeout) {
            clearTimeout(this._pollTimeout);
          }
          this._pollTimeout = setTimeout(this.doPoll, 500);
        }
      );
    }
  }

  forcePoll() {
    if (!this.options.forceWebsocket) {
      this.forcedPoll = true;
      this.usingWebsocket = false;
      this.polling = true;
      this.sid = null;
      this.doPoll();
    }
  }

  doWebsocket() {
    // Technically we should include the sid in the websocket URL;
    // but I don't want to reimplement socket.io's session handling...
    this.ws = this.connectionService.createWebsocket(this.wsUrl, { autoReconnect: false });
    this.ws.on('open', () => {
      this.forcedPoll = false;
      if (this.ws.readyState === 1) {
        this.ws.send('40');
      }
      this.emit('websocket_connected');
    });
    this.ws.on('close', () => {
      this.emit('websocket_disconnected');
      this.sid = null;
      if (!this.polling) {
        if (this.options.forceWebsocket) {
          this.doWebsocket();
        }
        else {
          this.usingWebsocket = false;
          this.polling = true;
          this.forcedPoll = false;
          this.sid = null;
          this.doPoll();
        }
      }
    });
    this.ws.on('message', (data) => {
      if (data.data) {
        this._wsDecoder.add(data.data);
      }
      else if (typeof(data.buffer !== 'undefined')) {
        this._wsDecoder.add(data.toString());
      }
    });
  }

  handlePacket(isWebsocket, packet) {
    if (packet.type === 4 && packet.data) {
      if (isWebsocket && !this.forcedPoll) {
        this.polling = false;
      }

      if (Array.isArray(packet.data)) {
        const [event, data] = packet.data;

        if (this._rooms[packet.id]) {
          this._rooms[packet.id](data, event);
        }
        else {
          this.emit('packet', [event, data]);
          if (event !== 'packet') {
            try {
              this.emit(event, JSON.parse(data));
            }
            catch {
              this.emit(event, data);
            }
          }
        }
      }

    }
    else if (packet.type === 2) {
      this.ws.send('3');
    }
    else if (packet.type === 3) {
      // Successful ping response from websocket
      this.polling = false;
    }
    else if (packet.type === 0) {
      if (!isWebsocket && packet.data.sid) {
        this.sid = encodeURIComponent(packet.data.sid);
      }
      if (packet.data.upgrades.includes('websocket') && !this.usingWebsocket && !this.forcedPoll) {
        this.usingWebsocket = true;
        this.doWebsocket();
      }
    }
  }

  join(room, callback) {
    const myIndex = this._roomsIndex++;
    const encoded = this._encoder.encode({
      type: `42${myIndex}`,
      nsp: '/',
      data: ['join', room]
    });

    this._rooms[`3${myIndex}`] = callback;

    if (this.usingWebsocket) {
      this.ws.send(encoded[0]);
    }
    else {
      console.error('Non-WS sending not yet supported'); //eslint-disable-line no-console
    }
  }

  stop() {
    this.ws?.close();
    if (this._pollTimeout) {
      clearTimeout(this._pollTimeout);
    }
  }

}
